import React, { useMemo, useState } from "react";
import {
  Users,
  UserCheck,
  IndianRupee,
  Clock,
  Download,
  FileBarChart,
  CalendarDays,
  TrendingUp,
  ChevronDown,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import "./Reports.css";

const registrationData = [
  { day: "01 Oct", members: 24 },
  { day: "02 Oct", members: 32 },
  { day: "03 Oct", members: 28 },
  { day: "04 Oct", members: 41 },
  { day: "05 Oct", members: 36 },
  { day: "06 Oct", members: 49 },
  { day: "07 Oct", members: 43 },
  { day: "08 Oct", members: 55 },
  { day: "09 Oct", members: 47 },
  { day: "10 Oct", members: 62 },
  { day: "11 Oct", members: 58 },
  { day: "12 Oct", members: 71 },
];

const revenueData = [
  { day: "01 Oct", revenue: 12000 },
  { day: "02 Oct", revenue: 18500 },
  { day: "03 Oct", revenue: 15000 },
  { day: "04 Oct", revenue: 22000 },
  { day: "05 Oct", revenue: 19500 },
  { day: "06 Oct", revenue: 28000 },
  { day: "07 Oct", revenue: 24500 },
  { day: "08 Oct", revenue: 32000 },
  { day: "09 Oct", revenue: 27000 },
  { day: "10 Oct", revenue: 36000 },
  { day: "11 Oct", revenue: 31000 },
  { day: "12 Oct", revenue: 42500 },
];

const reportRecords = [
  {
    id: "RPT001",
    member: "Arun Kumar",
    category: "Membership",
    date: "2026-10-12",
    amount: 2500,
    status: "Completed",
  },
  {
    id: "RPT002",
    member: "Priya S",
    category: "Registration",
    date: "2026-10-12",
    amount: 0,
    status: "Approved",
  },
  {
    id: "RPT003",
    member: "Karthik M",
    category: "Payment",
    date: "2026-10-11",
    amount: 1500,
    status: "Completed",
  },
  {
    id: "RPT004",
    member: "Divya R",
    category: "Registration",
    date: "2026-10-10",
    amount: 0,
    status: "Pending",
  },
  {
    id: "RPT005",
    member: "Senthil K",
    category: "Membership",
    date: "2026-10-09",
    amount: 5000,
    status: "Completed",
  },
  {
    id: "RPT006",
    member: "Kavitha P",
    category: "Payment",
    date: "2026-10-08",
    amount: 2500,
    status: "Pending",
  },
  {
    id: "RPT007",
    member: "Prakash T",
    category: "Registration",
    date: "2026-10-06",
    amount: 0,
    status: "Approved",
  },
  {
    id: "RPT008",
    member: "Meena S",
    category: "Membership",
    date: "2026-10-03",
    amount: 1500,
    status: "Completed",
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (value) =>
  new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const Reports = () => {
  const [period, setPeriod] = useState("30");
  const [category, setCategory] = useState("All");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filteredRecords = useMemo(() => {
    const today = new Date("2026-10-14T00:00:00");
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - Number(period));

    return reportRecords.filter((record) => {
      const recordDate = new Date(`${record.date}T00:00:00`);

      const matchesDate =
        recordDate >= startDate && recordDate <= today;

      const matchesCategory =
        category === "All" || record.category === category;

      const matchesStatus =
        status === "All" || record.status === status;

      const matchesSearch =
        record.member.toLowerCase().includes(search.toLowerCase()) ||
        record.id.toLowerCase().includes(search.toLowerCase());

      return (
        matchesDate &&
        matchesCategory &&
        matchesStatus &&
        matchesSearch
      );
    });
  }, [period, category, status, search]);

  const totalAmount = filteredRecords.reduce(
    (sum, record) => sum + record.amount,
    0
  );

  const totalMembers = filteredRecords.filter(
    (record) => record.category === "Registration"
  ).length;

  const completedPayments = filteredRecords.filter(
    (record) =>
      record.category === "Payment" &&
      record.status === "Completed"
  ).length;

  const pendingCount = filteredRecords.filter(
    (record) => record.status === "Pending"
  ).length;

  const pageSize = 5;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecords.length / pageSize)
  );

  const currentPage = Math.min(page, totalPages);
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const resetFilters = () => {
    setPeriod("30");
    setCategory("All");
    setStatus("All");
    setSearch("");
    setPage(1);
  };

  const handleExport = () => {
    const headers = [
      "Report ID",
      "Member Name",
      "Category",
      "Date",
      "Amount",
      "Status",
    ];

    const rows = filteredRecords.map((record) => [
      record.id,
      record.member,
      record.category,
      record.date,
      record.amount,
      record.status,
    ]);

    const escapeCSV = (value) =>
      `"${String(value).replace(/"/g, '""')}"`;

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCSV).join(","))
      .join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "salem-kongu-matrimony-report.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <div className="reports-breadcrumb">
            Dashboard <span>/</span> Reports
          </div>
          <h1>Reports & Analytics</h1>
          <p>
            Track member registrations, memberships, and platform revenue.
          </p>
        </div>

        <button
          type="button"
          className="reports-export-button"
          onClick={handleExport}
        >
          <Download size={17} />
          Export CSV
        </button>
      </div>

      <div className="reports-filter-card">
        <div className="reports-filter-title">
          <CalendarDays size={18} />
          <span>Report Filters</span>
        </div>

        <div className="reports-filter-controls">
          <div className="reports-filter-field">
            <label htmlFor="report-period">Date Range</label>
            <div className="reports-select-wrap">
              <select
                id="report-period"
                value={period}
                onChange={(event) => {
                  setPeriod(event.target.value);
                  setPage(1);
                }}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="365">Last 365 days</option>
              </select>
              <ChevronDown size={15} />
            </div>
          </div>

          <div className="reports-filter-field">
            <label htmlFor="report-category">Report Category</label>
            <div className="reports-select-wrap">
              <select
                id="report-category"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Categories</option>
                <option value="Registration">Registration</option>
                <option value="Membership">Membership</option>
                <option value="Payment">Payment</option>
              </select>
              <ChevronDown size={15} />
            </div>
          </div>

          <div className="reports-filter-field">
            <label htmlFor="report-status">Status</label>
            <div className="reports-select-wrap">
              <select
                id="report-status"
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
              >
                <option value="All">All Statuses</option>
                <option value="Completed">Completed</option>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
              </select>
              <ChevronDown size={15} />
            </div>
          </div>

          <button
            type="button"
            className="reports-reset-button"
            onClick={resetFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="reports-stats-grid">
        <div className="reports-stat-card">
          <div className="reports-stat-top">
            <span className="reports-stat-label">New Registrations</span>
            <span className="reports-stat-icon blue">
              <Users size={20} />
            </span>
          </div>
          <h2>{totalMembers}</h2>
          <p>In selected records</p>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-top">
            <span className="reports-stat-label">Completed Payments</span>
            <span className="reports-stat-icon green">
              <UserCheck size={20} />
            </span>
          </div>
          <h2>{completedPayments}</h2>
          <p>Successful payment records</p>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-top">
            <span className="reports-stat-label">Recorded Revenue</span>
            <span className="reports-stat-icon purple">
              <IndianRupee size={20} />
            </span>
          </div>
          <h2>{formatCurrency(totalAmount)}</h2>
          <p>From filtered records</p>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-top">
            <span className="reports-stat-label">Pending Records</span>
            <span className="reports-stat-icon orange">
              <Clock size={20} />
            </span>
          </div>
          <h2>{pendingCount}</h2>
          <p>Awaiting completion</p>
        </div>
      </div>

      <div className="reports-charts-grid">
        <div className="reports-chart-card">
          <div className="reports-card-heading">
            <div>
              <h3>Member Registrations</h3>
              <p>Daily registration trend</p>
            </div>
            <span className="reports-chart-badge">
              <TrendingUp size={14} /> Members
            </span>
          </div>

          <div className="reports-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={registrationData}
                margin={{ top: 10, right: 8, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="registrationGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#3b82f6"
                      stopOpacity={0.22}
                    />
                    <stop
                      offset="100%"
                      stopColor="#3b82f6"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="#edf2f7"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="members"
                  name="Members"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  fill="url(#registrationGradient)"
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="reports-chart-card">
          <div className="reports-card-heading">
            <div>
              <h3>Revenue Overview</h3>
              <p>Daily recorded revenue</p>
            </div>
            <span className="reports-chart-badge revenue">
              <IndianRupee size={14} /> INR
            </span>
          </div>

          <div className="reports-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={revenueData}
                margin={{ top: 10, right: 5, left: -15, bottom: 0 }}
              >
                <CartesianGrid
                  stroke="#edf2f7"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(value) => `${value / 1000}k`}
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Revenue"]}
                />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill="#10b981"
                  radius={[5, 5, 0, 0]}
                  maxBarSize={27}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="reports-table-card">
        <div className="reports-table-heading">
          <div>
            <h3>Detailed Report</h3>
            <p>Review filtered registration, membership, and payment records.</p>
          </div>

          <div className="reports-record-count">
            {filteredRecords.length} records
          </div>
        </div>

        <div className="reports-table-toolbar">
          <input
            type="search"
            placeholder="Search member or report ID..."
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            aria-label="Search report records"
          />
          <span>
            Total amount: <strong>{formatCurrency(totalAmount)}</strong>
          </span>
        </div>

        <div className="reports-table-scroll">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Report ID</th>
                <th>Member Name</th>
                <th>Category</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {paginatedRecords.map((record) => (
                <tr key={record.id}>
                  <td className="reports-id">{record.id}</td>
                  <td className="reports-member-name">{record.member}</td>
                  <td>
                    <span
                      className={`reports-category ${record.category.toLowerCase()}`}
                    >
                      {record.category}
                    </span>
                  </td>
                  <td>{formatDate(record.date)}</td>
                  <td className="reports-amount">
                    {formatCurrency(record.amount)}
                  </td>
                  <td>
                    <span
                      className={`reports-status ${record.status.toLowerCase()}`}
                    >
                      {record.status}
                    </span>
                  </td>
                </tr>
              ))}

              {paginatedRecords.length === 0 && (
                <tr>
                  <td colSpan="6" className="reports-empty">
                    <FileBarChart size={28} />
                    <strong>No reports found</strong>
                    <span>Try changing the date range or filters.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="reports-pagination">
          <span>
            Showing{" "}
            {filteredRecords.length === 0
              ? 0
              : (currentPage - 1) * pageSize + 1}
            {" - "}
            {Math.min(currentPage * pageSize, filteredRecords.length)} of{" "}
            {filteredRecords.length}
          </span>

          <div>
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((previous) => previous - 1)}
            >
              Previous
            </button>
            <span className="reports-page-number">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((previous) => previous + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;